import { useState, useEffect, useCallback } from 'react';
import { paymentsAPI, patientsAPI, patientDashboardAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  History, Search, User, Calendar, IndianRupee, Printer, Filter, X, FileText,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { printReceiptHTML } from './InvoicePrint';

const InvoiceHistoryPage = () => {
  const { hospital } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);

  // Filters
  const [patientQ, setPatientQ] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [staffId, setStaffId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [receiptQ, setReceiptQ] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await paymentsAPI.staffList();
        setStaffList(r.data || []);
      } catch { /* optional */ }
    })();
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedPatient) params.patient_id = selectedPatient.id;
      if (staffId && staffId !== 'all') params.collected_by = staffId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (receiptQ.trim()) params.receipt_number = receiptQ.trim();
      const res = await paymentsAPI.history(params);
      setRows(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to search invoices');
    }
    setLoading(false);
  }, [selectedPatient, staffId, dateFrom, dateTo, receiptQ]);

  // initial load — last 200 receipts for the hospital
  useEffect(() => { search(); }, []); // eslint-disable-line

  const handlePatientSearch = async (q) => {
    setPatientQ(q);
    if (q.length < 2) { setPatientResults([]); return; }
    try {
      const res = await patientDashboardAPI.search(q);
      setPatientResults(res.data || []);
    } catch { /* ignore */ }
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    setPatientQ('');
    setPatientResults([]);
  };

  const resetFilters = () => {
    clearPatient();
    setStaffId('all');
    setDateFrom('');
    setDateTo('');
    setReceiptQ('');
    setTimeout(() => search(), 0);
  };

  const handleReprint = async (p) => {
    try {
      const patientRes = await patientsAPI.get(p.patient_id);
      printReceiptHTML({ receipt: p, patient: patientRes.data, hospital: hospital || {} });
    } catch {
      printReceiptHTML({
        receipt: p,
        patient: { first_name: p.patient_name, patient_id: p.patient_uhid },
        hospital: hospital || {},
      });
    }
  };

  // v37b2 — show NET amount (after refunds) in the totals so the bill list reflects refunded money.
  const total = rows.reduce((s, r) => {
    const net = r.net_amount != null ? r.net_amount : r.amount;
    return s + (net || 0);
  }, 0);
  const totalRefunded = rows.reduce((s, r) => s + (r.refunded_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="invoice-history-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <History size={22} className="text-teal-600" /> Invoice / Receipt History
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Reprint any past receipt — filter by patient, staff, date range, or receipt number</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Patient */}
          <div className="lg:col-span-2">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Patient</label>
            {selectedPatient ? (
              <div className="flex items-center gap-2 p-2 bg-teal-50 rounded-md">
                <User size={14} className="text-teal-600" />
                <span className="text-xs font-medium text-slate-700 flex-1 truncate">
                  {selectedPatient.first_name} {selectedPatient.last_name} · {selectedPatient.patient_id}
                </span>
                <button onClick={clearPatient} className="text-slate-400 hover:text-red-500" data-testid="clear-patient-btn">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by name, UHID, or phone..."
                  value={patientQ}
                  onChange={(e) => handlePatientSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="history-patient-search"
                />
                {patientResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto bg-white border rounded-lg shadow-lg">
                    {patientResults.map(p => (
                      <button key={p.id} onClick={() => { setSelectedPatient(p); setPatientResults([]); setPatientQ(''); }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 text-xs"
                        data-testid={`history-patient-${p.id}`}>
                        <span className="font-medium text-slate-800">{p.first_name} {p.last_name}</span>
                        <span className="text-slate-400 ml-2">{p.patient_id} · {p.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Staff */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Collected By</label>
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger className="h-9" data-testid="history-staff-select">
                <SelectValue placeholder="All staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All staff</SelectItem>
                {staffList.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`staff-opt-${s.id}`}>
                    {s.name} ({s.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Receipt # */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Receipt #</label>
            <Input
              placeholder="PH-RCP-..."
              value={receiptQ}
              onChange={e => setReceiptQ(e.target.value)}
              className="h-9 font-mono text-xs"
              data-testid="history-receipt-input"
            />
          </div>

          {/* Date range */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Date From</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="h-9"
              data-testid="history-date-from"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Date To</label>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="h-9"
              data-testid="history-date-to"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={resetFilters} data-testid="reset-filters-btn">Reset</Button>
          <Button onClick={search} className="bg-teal-600 hover:bg-teal-700 gap-2" data-testid="apply-filters-btn">
            <Search size={14} /> Apply
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><FileText size={18} className="text-blue-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Receipts Matching</p>
              <p className="text-xl font-bold text-slate-900" data-testid="history-count">{rows.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center"><IndianRupee size={18} className="text-teal-600" /></div>
            <div>
              <p className="text-xs text-slate-500">Filtered Total <span className="text-[10px] text-slate-400">(net of refunds)</span></p>
              <p className="text-xl font-bold text-slate-900" data-testid="history-total">Rs. {total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p>
              {totalRefunded > 0 && (
                <p className="text-[10px] text-rose-600 font-semibold mt-0.5" data-testid="history-refunded-total">
                  − Rs. {totalRefunded.toLocaleString(undefined, { maximumFractionDigits: 2 })} refunded
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Results table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Receipt #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Patient</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Method(s)</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Service</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Collected By</th>
              <th className="px-2 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400" data-testid="no-results">No receipts match the selected filters.</td></tr>
            ) : rows.map(p => {
              const methods = Array.isArray(p.splits) && p.splits.length
                ? p.splits.map(s => (s.method || '').toUpperCase()).join(' + ')
                : (p.payment_method || '').toUpperCase();
              return (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`history-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.receipt_number}</td>
                  <td className="px-4 py-3">
                    <p className="text-xs font-medium text-slate-800">{p.patient_name || '-'}</p>
                    <p className="text-[10px] text-slate-400">{p.patient_uhid || ''}</p>
                  </td>
                  <td className="px-4 py-3 font-bold text-slate-800">
                    {(() => {
                      const orig = p.amount || 0;
                      const refunded = p.refunded_amount || 0;
                      const net = p.net_amount != null ? p.net_amount : orig;
                      if (refunded > 0) {
                        return (
                          <div className="space-y-0.5">
                            <p className={net === 0 ? 'line-through text-slate-400' : 'text-slate-800'} data-testid={`history-amount-${p.id}`}>
                              Rs. {net.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                            <p className="text-[10px] text-rose-600 font-semibold" data-testid={`history-refund-badge-${p.id}`}>
                              − Rs. {refunded.toLocaleString(undefined, { maximumFractionDigits: 2 })} refunded
                            </p>
                            <p className="text-[9px] text-slate-400 font-normal">orig: Rs. {orig.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                          </div>
                        );
                      }
                      return <span data-testid={`history-amount-${p.id}`}>Rs. {orig.toLocaleString()}</span>;
                    })()}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{methods}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-40 truncate">{p.service_name || p.description || '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    <Calendar size={11} className="inline mr-1 text-slate-400" />
                    {p.created_at ? new Date(p.created_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{p.collected_by_name || '-'}</td>
                  <td className="px-2 py-3">
                    <button
                      onClick={() => window.open(`/invoice/print/${p.id}`, '_blank')}
                      title="Print Invoice"
                      className="p-1.5 rounded hover:bg-teal-50 text-emerald-600 hover:text-emerald-700"
                      data-testid={`history-reprint-${p.id}`}>
                      <Printer size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceHistoryPage;
